import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {createOnboardingProgress} from './onboarding.js';
import {createSmokingProfile} from './profile.js';
import {SMOKER_ENTITY_TYPE} from './smoker-store.js';
import {SMOKER_CAPABILITY_ENTITY_TYPE} from './smoker-capability-store.js';
import {THERMOMETER_ENTITY_TYPE} from './thermometer-store.js';
import {PROBE_ENTITY_TYPE} from './probe-store.js';
import {WOOD_INVENTORY_ENTITY} from './wood-inventory.js';
import {WOOD_FAVOURITE_ENTITY_TYPE} from './wood-favourites.js';

export const SMOKING_PROFILE_ENTITY_TYPE='smoking_profile';
export const ONBOARDING_PROGRESS_ENTITY_TYPE='onboarding_progress';

export const SMOKEHOUSE_LOCAL_ENTITY_TYPES=Object.freeze([
  SMOKING_PROFILE_ENTITY_TYPE,
  ONBOARDING_PROGRESS_ENTITY_TYPE,
  SMOKER_ENTITY_TYPE,
  SMOKER_CAPABILITY_ENTITY_TYPE,
  THERMOMETER_ENTITY_TYPE,
  PROBE_ENTITY_TYPE,
  WOOD_INVENTORY_ENTITY,
  WOOD_FAVOURITE_ENTITY_TYPE
]);

function requireDependencies(repository,queue){
  if(!repository?.get||!repository?.list||!repository?.put)throw new TypeError('repository with get/list/put is required');
  if(!queue?.enqueue||!queue?.markConflict||!queue?.listPending)throw new TypeError('sync queue contract is required');
}

function owner(value){
  const id=String(value||'').trim();
  if(!id)throw new TypeError('userId is required');
  return id;
}

function stripRepositoryFields(value={}){
  const copy={...value};
  delete copy.key;
  delete copy.entityType;
  return copy;
}

function mergeMeta(existing,domain,{id,now}){
  if(existing){
    return {
      ...stripRepositoryFields(existing),
      ...domain,
      id,
      userId:domain.userId||existing.userId
    };
  }
  return {
    ...createEntityMeta({id,now,syncStatus:SyncStatus.PENDING_CREATE}),
    ...domain,
    id
  };
}

function isConflict(value){
  return value?.syncStatus===SyncStatus.CONFLICT;
}

export function createSmokehouseOfflineStore({
  repository,
  queue,
  userId,
  now=()=>new Date().toISOString()
}={}){
  requireDependencies(repository,queue);
  const user=owner(userId);

  async function getProfile(){
    const existing=await repository.get(SMOKING_PROFILE_ENTITY_TYPE,user);
    if(!existing)return createSmokingProfile({userId:user});
    if(existing.userId!==user)throw new Error('SMOKING_PROFILE_OWNER_MISMATCH');
    return existing;
  }

  async function saveProfile(changes={}){
    const existing=await repository.get(SMOKING_PROFILE_ENTITY_TYPE,user);
    if(isConflict(existing))throw new Error('SMOKING_PROFILE_CONFLICT');
    const domain=createSmokingProfile({
      ...(existing?stripRepositoryFields(existing):{userId:user}),
      ...changes,
      userId:user
    });
    const entity=mergeMeta(existing,domain,{id:user,now:now()});
    return queue.enqueue(
      SMOKING_PROFILE_ENTITY_TYPE,
      entity,
      existing?'update':'create'
    );
  }

  async function getOnboarding(){
    const existing=await repository.get(ONBOARDING_PROGRESS_ENTITY_TYPE,user);
    if(!existing)return createOnboardingProgress({userId:user});
    if(existing.userId!==user)throw new Error('ONBOARDING_OWNER_MISMATCH');
    return existing;
  }

  async function saveOnboarding(progress){
    const existing=await repository.get(ONBOARDING_PROGRESS_ENTITY_TYPE,user);
    if(isConflict(existing))throw new Error('ONBOARDING_CONFLICT');
    const domain=createOnboardingProgress({...progress,userId:user});
    const entity=mergeMeta(existing,domain,{id:user,now:now()});
    return queue.enqueue(
      ONBOARDING_PROGRESS_ENTITY_TYPE,
      entity,
      existing?'update':'create'
    );
  }

  async function snapshot(){
    const result={};
    for(const type of SMOKEHOUSE_LOCAL_ENTITY_TYPES){
      const rows=await repository.list(type);
      result[type]=Object.freeze((rows||[]).filter(row=>!row.userId||row.userId===user));
    }
    return Object.freeze(result);
  }

  async function pendingSummary(){
    const rows=await queue.listPending();
    const counts={};
    for(const type of SMOKEHOUSE_LOCAL_ENTITY_TYPES)counts[type]=0;
    for(const row of rows||[]){
      if(SMOKEHOUSE_LOCAL_ENTITY_TYPES.includes(row.entityType)){
        counts[row.entityType]=(counts[row.entityType]||0)+1;
      }
    }
    return Object.freeze(counts);
  }

  async function markConflict(entityType,id,conflict){
    if(!SMOKEHOUSE_LOCAL_ENTITY_TYPES.includes(entityType))throw new TypeError('Unsupported Smokehouse entity type');
    const existing=await repository.get(entityType,id);
    if(!existing)throw new TypeError('Smokehouse entity not found');
    if(existing.userId&&existing.userId!==user)throw new Error('SMOKEHOUSE_OWNER_MISMATCH');
    return queue.markConflict(entityType,existing,conflict);
  }

  return Object.freeze({
    getProfile,
    saveProfile,
    getOnboarding,
    saveOnboarding,
    snapshot,
    pendingSummary,
    markConflict
  });
}
